import { useEffect, useRef } from 'react';

/** Hands a step's save routine to the wizard, or clears it with null. */
export type RegisterStepSave = (save: (() => Promise<void>) | null) => void;

/**
 * Lets a wizard step hand its save routine to QuickSetupWizard so the footer's
 * "Save & Continue" persists the step before advancing.
 *
 * Steps that hold user input in local state have no other way to be saved: the
 * footer button only marks progress, so without this the data is dropped when
 * the step unmounts. Registering is what makes the button's label true.
 *
 * The registration is stable for the life of the step — the wrapper reads the
 * handler through a ref, so a save closing over the newest state is called
 * without re-registering on every render.
 */
export function useRegisterStepSave(
  registerSave: RegisterStepSave | undefined,
  save: () => Promise<void>,
): void {
  const saveRef = useRef(save);

  // Kept current in an effect rather than during render; effects run before the
  // user can reach the footer button, so the registered wrapper never calls a
  // stale closure.
  useEffect(() => {
    saveRef.current = save;
  });

  useEffect(() => {
    if (!registerSave) return;
    registerSave(() => saveRef.current());
    return () => registerSave(null);
  }, [registerSave]);
}
